/**
 * Role-messages API client (Milestone 4-B, spec §3.10).
 *
 * Wraps gateway endpoints in apps/chaos-gateway/src/role-messages.ts:
 *   GET   /api/orgs/:orgId/role-messages
 *   POST  /api/orgs/:orgId/role-messages
 *   PATCH /api/role-messages/:id/respond
 */
import { apiJson } from './client';

export type RoleMessageKind =
  | 'question'
  | 'approval_request'
  | 'escalation'
  | 'report'
  | 'notification';

export type RoleMessageStatus = 'pending' | 'answered' | 'declined' | 'expired';

export type RoleMessageUrgency = 'low' | 'normal' | 'urgent';

export type RoleMessageActorKind = 'user' | 'role';

export interface RoleMessage {
  id: string;
  orgId: string;
  fromKind: RoleMessageActorKind;
  fromUserId: string | null;
  fromRoleId: string | null;
  fromSessionId: string | null;
  toKind: RoleMessageActorKind;
  toUserId: string | null;
  toRoleId: string | null;
  onCardId: string | null;
  kind: RoleMessageKind;
  urgency: RoleMessageUrgency;
  subject: string | null;
  body: string;
  artifactRefs: unknown;
  status: RoleMessageStatus;
  respondedAt: string | null;
  responseBody: string | null;
  respondedByKind: RoleMessageActorKind | null;
  respondedByUserId: string | null;
  respondedByRoleId: string | null;
  createdAt: string;
  /** Server-authoritative action availability for the authenticated viewer. */
  canRespond: boolean;
}

export interface ListRoleMessagesParams {
  toRoleId?: string;
  toUserId?: string;
  status?: RoleMessageStatus;
  cardId?: string;
  kind?: RoleMessageKind;
  limit?: number;
}

export interface CreateRoleMessageInput {
  toKind: RoleMessageActorKind;
  toUserId?: string | null;
  toRoleId?: string | null;
  onCardId?: string | null;
  kind: RoleMessageKind;
  urgency?: RoleMessageUrgency;
  subject?: string | null;
  body: string;
  artifactRefs?: unknown;
}

export interface RespondRoleMessageInput {
  responseBody: string;
  status?: 'answered' | 'declined';
}

interface RoleMessagesListResponse {
  messages: RoleMessage[];
}
interface RoleMessageResponse {
  message: RoleMessage;
}

function buildQuery(params: ListRoleMessagesParams = {}): string {
  const qs = new URLSearchParams();
  if (params.toRoleId) qs.set('to_role_id', params.toRoleId);
  if (params.toUserId) qs.set('to_user_id', params.toUserId);
  if (params.status) qs.set('status', params.status);
  if (params.cardId) qs.set('card_id', params.cardId);
  if (params.kind) qs.set('kind', params.kind);
  if (params.limit) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const roleMessagesApi = {
  list(orgId: string, params: ListRoleMessagesParams = {}): Promise<RoleMessage[]> {
    return apiJson<RoleMessagesListResponse>(
      `/orgs/${orgId}/role-messages${buildQuery(params)}`,
    ).then((r) => r.messages);
  },
  create(orgId: string, input: CreateRoleMessageInput): Promise<RoleMessage> {
    return apiJson<RoleMessageResponse>(`/orgs/${orgId}/role-messages`, {
      method: 'POST',
      json: input,
    }).then((r) => r.message);
  },
  respond(id: string, input: RespondRoleMessageInput): Promise<RoleMessage> {
    return apiJson<RoleMessageResponse>(`/role-messages/${id}/respond`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.message);
  },
};

export const roleMessagesKeys = {
  all: ['role-messages'] as const,
  list: (orgId: string, params: ListRoleMessagesParams = {}) =>
    [...roleMessagesKeys.all, 'list', orgId, params] as const,
  byCard: (orgId: string, cardId: string) =>
    [...roleMessagesKeys.all, 'card', orgId, cardId] as const,
};
